import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { X } from './icons';
import { IconButton } from './IconButton';
import { WS_COLOR, WS_SPACE } from './tokens';

export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Fixed actions at the foot; the body scrolls, they do not. */
  actions?: ReactNode;
  /** `full` covers the screen (narrow widths); a number is a side sheet of that many px. */
  width?: 'full' | number;
}

/**
 * Where a record opens when there is no room for a detail pane: a full-height sheet anchored
 * to the right edge (or the whole screen on a phone). Square corners are correct here — it
 * is an edge-anchored surface (CLAUDE.md 1.9.2). The header and the action row are fixed;
 * only the body scrolls.
 */
export function Sheet({ open, title, onClose, children, actions, width = 'full' }: SheetProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-label': title,
        sx: { width: width === 'full' ? '100%' : width, maxWidth: '100%', borderRadius: 0 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 5,
            height: 56,
            flexShrink: 0,
            borderBottom: `1px solid ${WS_COLOR.border}`,
          }}
        >
          <Typography variant="h2" component="h2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: `${WS_SPACE.xl - 8}px 20px` }}>
          {children}
        </Box>
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              px: 5,
              py: 3,
              flexShrink: 0,
              borderTop: `1px solid ${WS_COLOR.border}`,
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
}
